/* eslint-disable @stylistic/ts/padding-line-between-statements */
import { HttpParams } from "@angular/common/http";
import { SORT_DIRECTIONS } from "../types/sorting-directions";
// eslint-disable-next-line @tseslint/no-shadow
import { and, define, equals, greaterOrEqual, lessOrEqual, Node, or } from "@ogs-gmbh/rsql";
import { TableFilterConfig } from "../types/filter/table-filter-config.type";
import { TableConfig } from "../types/table-config";
import { getTableColumnsSortedByFields, TableColumn } from "../types/column/header.type";
import { TABLE_FILTER_TYPES } from "../types/filter/table-filter.type";
import { COMBINATION_STRATEGIES } from "../types/filter/table-filter-group.type";


function getTableFilterParams (filterConfig: TableFilterConfig): HttpParams {
  let params: HttpParams = new HttpParams();

  let filterNodes: Node[] = [];
  const filterGroupNodes: Node[] = [];

  for (const filterGroup of filterConfig.filterGroups) {
    for (const filter of filterGroup.children) {
      switch (filter.filterValue.filterType) {
        case TABLE_FILTER_TYPES.START_DATE: {
          if (filter.filterValue.date) {
            filterNodes.push(
              greaterOrEqual(filter.paramName, filter.filterValue.date.toISOString())
            );
          }
          break;
        }
        case TABLE_FILTER_TYPES.END_DATE: {
          if (filter.filterValue.date) {
            filterNodes.push(
              lessOrEqual(filter.paramName, filter.filterValue.date.toISOString())
            );
          }
          break;
        }
        case TABLE_FILTER_TYPES.SELECT: {
          if (filter.filterValue.isSelected) {
            filterNodes.push(
              equals(filter.paramName, String(filter.filterValue.value))
            );
          }
        }
      }
    }
    if (filterNodes.length > 0) {
      switch (filterGroup.combinationStrategy) {
        case COMBINATION_STRATEGIES.AND: {
          filterGroupNodes.push(and(...filterNodes));
          filterNodes = [];
          break;
        }
        case COMBINATION_STRATEGIES.OR: {
          filterGroupNodes.push(or(...filterNodes));
          filterNodes = [];
          break;
        }
      }
    }
  }

  if (filterGroupNodes.length > 0)
    params = params.set("filter", define(and(...filterGroupNodes)).toString());

  return params;
}


function getSortValuesForColumn (header: TableColumn): string[] | undefined {
  if (!header.isRequiredField && (!header.options.isVisible || !header.dataFields || header.dataFields.length === 0))
    return;

  const values: string[] = [];

  switch (header.options.sortDirection) {
    case SORT_DIRECTIONS.DESCENDING: {
      // eslint-disable-next-line @tseslint/no-non-null-assertion
      values.push(...header.dataFields!.flatMap((field: string) => `-${ field }`));
      break;
    }

    case SORT_DIRECTIONS.ASCENDING: {
      // eslint-disable-next-line @tseslint/no-non-null-assertion
      values.push(...(header.dataFields!).flat());
      break;
    }

    default:
  }

  return values;
}

export function buildTableApiParams (tableConfig: TableConfig, limitResponseCount: number, dataOffset: number, options: { fieldsToSkip?: string[]; ignoreFilter?: boolean; } = {}): HttpParams {
  let params: HttpParams = new HttpParams();

  const fields: string[] = tableConfig.columnConfig.columns
    .filter((header: TableColumn) => Boolean(header.isRequiredField) || !options.fieldsToSkip?.includes(header.field) && header.options.isVisible && header.dataFields)
    // eslint-disable-next-line @tseslint/no-non-null-assertion
    .flatMap((header: TableColumn) => header.dataFields!);
  // General
  params = params.set("limit", limitResponseCount);
  params = params.set("offset", dataOffset);

  // Table --> ignoreFilter check
  if (!options.ignoreFilter && tableConfig.options?.searchTerm)
    params = params.set("fulltext", tableConfig.options.searchTerm);

  // Fields

  const headersSortedByOptions: TableColumn[] = getTableColumnsSortedByFields(tableConfig.columnConfig.fieldsSortedByOptions, tableConfig.columnConfig.columns);

  const sortValues: string[] = [];
  for (const header of headersSortedByOptions) {
    const sortValuesForColumns: string[] | undefined = getSortValuesForColumn(header);

    if (sortValuesForColumns)
      sortValues.push(...sortValuesForColumns);
  }


  if (tableConfig.filterConfig) {
    const filterValues: string[] | null = getTableFilterParams(tableConfig.filterConfig).getAll("filter");

    if (filterValues && filterValues.length > 0)
      params = params.appendAll({ filter: filterValues });
  }

  if (fields.length > 0)
    params = params.set("fields", fields.join(","));

  if (sortValues.length > 0)
    params = params.set("sort", sortValues.join(","));


  return params;
}

